// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createStore,
	type Action,
	type OverrideContainerComponent,
	type Store as SweetStateStore,
} from '@atlassian/react-sweet-state';
import {
	setViewModeInLocalStorage,
	type ViewMode,
} from '@jira/issue__issue-preview-view-mode-storage/src/getViewModeFromLocalStorage.tsx';

// null/undefined represent the pre-hydration state on the server, where localStorage cannot be read
// and no preview-mode preference is known yet.
export type HydratedViewMode = ViewMode | null | undefined;

export type IssuePreviewViewModeActions = Readonly<{
	setDetailViewAsModal: (willSave?: boolean) => void;
	setDetailViewAsSidebar: (willSave?: boolean) => void;
}>;

type State = {
	viewMode: HydratedViewMode;
};

type ContainerProps = {
	initialViewMode: HydratedViewMode;
};

type Actions = {
	initialiseViewMode: () => Action<State, ContainerProps>;
	setDetailViewAsModal: (willSave?: boolean) => Action<State, ContainerProps>;
	setDetailViewAsSidebar: (willSave?: boolean) => Action<State, ContainerProps>;
};

const initialState: State = {
	viewMode: null,
};

export const issuePreviewViewModeStore: SweetStateStore<State, Actions> = createStore<
	State,
	Actions
>({
	name: 'issue-preview-view-mode',
	initialState,
	actions: {
		initialiseViewMode:
			() =>
			({ setState }, { initialViewMode }) => {
				setState({ viewMode: initialViewMode });
			},
		setDetailViewAsModal:
			(willSave) =>
			({ setState }) => {
				if (willSave ?? true) {
					setViewModeInLocalStorage('MODAL');
				}
				setState({ viewMode: 'MODAL' });
			},
		setDetailViewAsSidebar:
			(willSave) =>
			({ setState }) => {
				if (willSave ?? true) {
					setViewModeInLocalStorage('SIDEBAR');
				}
				setState({ viewMode: 'SIDEBAR' });
			},
	},
	behavior: 'strict',
});

export const IssuePreviewViewModeContainer: OverrideContainerComponent<ContainerProps> =
	createContainer<State, Actions, ContainerProps>(issuePreviewViewModeStore, {
		displayName: 'IssuePreviewViewModeContainer',
		onInit: () => (api) => {
			api.dispatch(issuePreviewViewModeStore.actions.initialiseViewMode());
		},
		onUpdate: () => (api) => {
			api.dispatch(issuePreviewViewModeStore.actions.initialiseViewMode());
		},
		getIsConcurrentSafe: () => true,
	});
